import { Form } from '@models/Form'
import { formSchema } from '@models/schema'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  deleteForm as deleteFormAPI,
  fetchFormById,
  fetchForms,
  updateForm as updateFormAPI,
} from '@services/FormService'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { AppDispatch, RootState } from './store'

const initialState: SBState<Form> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'form',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getFormsSuccess,
  setQuery: setFormQuery,
  reset: resetFormState,
  resetQueryAndIds: resetFormQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: number]: Form }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectForms = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedForm = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectFormById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectFormsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedForm =
  (form: Form | number) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedId(getIdOrModelId<Form>(form)))

export const getForms = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchForms(params),
    [formSchema],
    slice.actions
  )

export const getFormWithId = (id: number) =>
  SBAPI.FetchDispatched(fetchFormById(id), formSchema, slice.actions)

export const updateForm = (form: Form) =>
  SBAPI.UpdateDispatched<Form>(updateFormAPI(form), form, slice.actions)

export const deleteForm = (form: Form) =>
  SBAPI.DestroyDispatched<Form>(deleteFormAPI(form), form, slice.actions)
